import { buildPageviewUrl } from "@aix/webutils/src/routing"
import { useLocation } from "@tanstack/react-router"
import posthog from "posthog-js"
import { PostHogProvider as PostHogProviderComponent } from "posthog-js/react"
import { useEffect } from "react"

import { capturePostHogEvent, initPostHog, POSTHOG_ENABLED } from "./posthogClient"

// Defer PostHog initialization to after first paint
// This prevents blocking the main thread during initial render
if (typeof window !== "undefined") {
  if ("requestIdleCallback" in window) {
    // Use requestIdleCallback for best performance - runs when browser is idle
    requestIdleCallback(initPostHog, { timeout: 2000 })
  } else {
    // Fallback for Safari - delay 1 second after load
    setTimeout(initPostHog, 1000)
  }
}

export default function PostHogClientProvider({ children }: { children: React.ReactNode }) {
  const { pathname, searchStr } = useLocation()

  /** @see https://posthog.com/tutorials/nextjs-app-directory-analytics#capturing-pageviews-on-the-client-side */
  useEffect(() => {
    if (pathname) {
      const url = buildPageviewUrl(window.origin, { pathname, searchStr })
      if (posthog && POSTHOG_ENABLED) {
        capturePostHogEvent("$pageview", {
          $current_url: url,
        })
      }
    }
  }, [pathname, searchStr])

  return POSTHOG_ENABLED ? (
    <PostHogProviderComponent client={posthog}>{children}</PostHogProviderComponent>
  ) : (
    <>{children}</>
  )
}
